import { GoogleAuthProvider, signInWithCredential } from 'firebase/auth';

import { env } from '@/config/env';

import { firebaseAuth } from './app';

/**
 * One-click sign-in for local development against the emulators.
 *
 * This exists so `npm start` needs no Google popup: clicking "Anmelden" signs
 * in a fixed developer account and grants it the tournament-director role via
 * the Auth emulator's admin API.
 *
 * It is guarded by {@link isDevSignInEnabled}, which is only ever true when the
 * app talks to the emulators. In a production build `env.useEmulators` is baked
 * to `false`, so this path cannot run against real data — there is no way to
 * skip the real Google login in production.
 */
const DEV_USER = {
  sub: 'dev-turnierleitung',
  email: 'dev@localhost',
  name: 'Dev-Turnierleitung',
} as const;

const EMULATOR_HOST = '127.0.0.1:9099';

export function isDevSignInEnabled(): boolean {
  return env.useEmulators;
}

async function grantAdminClaim(uid: string): Promise<void> {
  const url =
    `http://${EMULATOR_HOST}/identitytoolkit.googleapis.com/v1/projects/` +
    `${env.firebase.projectId}/accounts:update?key=${env.firebase.apiKey}`;

  await fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: 'Bearer owner',
    },
    body: JSON.stringify({
      localId: uid,
      customAttributes: JSON.stringify({ role: 'admin' }),
    }),
  });
}

/**
 * Signs in the developer account and refreshes the token so the freshly granted
 * admin claim is present immediately. Throws if called outside the emulator
 * setup, which should never happen because the caller checks
 * {@link isDevSignInEnabled} first.
 */
export async function signInForDevelopment(): Promise<void> {
  if (!isDevSignInEnabled()) {
    throw new Error('Die Entwicklungs-Anmeldung ist außerhalb der Emulatoren nicht verfügbar.');
  }

  const idToken = JSON.stringify({
    sub: DEV_USER.sub,
    email: DEV_USER.email,
    email_verified: true,
    name: DEV_USER.name,
  });

  const credential = GoogleAuthProvider.credential(idToken);
  const result = await signInWithCredential(firebaseAuth, credential);

  await grantAdminClaim(result.user.uid);
  // Force a refresh so onIdTokenChanged delivers the token carrying the claim.
  await result.user.getIdToken(true);
}
